import { Fragment, useEffect, useId, useState, useSyncExternalStore } from 'react';
import { NavLink, matchPath, useLocation, useSearchParams } from 'react-router-dom';
import { config } from '@/config/env';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AppIcon } from '@/shared/components/ui/AppIcon/AppIcon';
import type { SidebarProjectSlotProps } from '@/shared/components/layout/Sidebar';
import { modelSectionOf, visibleModelSections } from '@/features/projects/utils/modelSections';
import { toProjectId } from '@/shared/types/common.types';
import { useCapabilities } from '../hooks/useCapabilities';
import { useModelKind } from '../hooks/useProjectDetails';
import { useWorkspaceProject } from '../hooks/useWorkspaceProjects';
import {
  readRecentProjects,
  recordRecentProject,
  subscribeRecentProjects,
} from '../utils/recentProjects';
import type { RecentProject } from '../utils/recentProjects';

interface ProjectSectionLink {
  readonly label: string;
  readonly to: string;
  readonly end?: boolean;
}

function sectionsOf(
  project: RecentProject,
  capabilities: { readonly projectDetails: boolean }
): readonly ProjectSectionLink[] {
  const links: ProjectSectionLink[] = [
    { label: 'Overview', to: ROUTES.projectHome(project.id), end: true },
    { label: 'Models', to: ROUTES.projectModels(project.id) },
  ];

  if (capabilities.projectDetails) {
    links.push(
      { label: 'Requirements', to: ROUTES.projectRequirements(project.id) },
      { label: 'Album Releases', to: ROUTES.projectAlbumReleases(project.id) }
    );
  }
  if (config.tasksBaseUrl !== null) links.push({ label: 'Requests', to: ROUTES.projectTasks(project.id) });
  if (project.role === 'ProjectAdmin') {
    links.push(
      { label: 'Members', to: ROUTES.projectMembers(project.id) },
      { label: 'Settings', to: ROUTES.projectSettings(project.id) }
    );
  }

  return links;
}

/**
 * Remembers the project the person is on, as soon as it has loaded.
 *
 * The page already asked for the project, so this shares its request. Nothing is recorded for
 * a project the person cannot open.
 */
function useRecordCurrentProject(owner: string, currentId: string | null): void {
  const query = useWorkspaceProject(toProjectId(currentId ?? ''), currentId !== null);
  const project = query.data;

  useEffect(() => {
    if (project === undefined) return;
    recordRecentProject(owner, {
      id: project.id,
      name: project.name,
      code: project.code,
      role: project.myRole,
    });
  }, [owner, project]);
}

/**
 * One of a project's models. The model that is open also lists its sections: the model page
 * shows one section at a time and this is how it is chosen, so the page needs no navigation of
 * its own.
 */
function ModelItem({
  projectId,
  modelId,
  title,
  isOpen,
  canManage,
  onNavigate,
}: {
  readonly projectId: string;
  readonly modelId: number;
  readonly title: string;
  readonly isOpen: boolean;
  /** Project admins also get the model's Settings. */
  readonly canManage: boolean;
  readonly onNavigate: () => void;
}): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const active = modelSectionOf(searchParams.get('tab'), searchParams.get('instance') !== null);
  const base = ROUTES.projectModel(projectId, modelId);
  // Only the open model's sections are drawn, so only it asks for its kind.
  const kind = useModelKind(isOpen ? projectId : null, modelId);

  return (
    <li className="project-tree__model">
      <NavLink
        to={base}
        end
        onClick={onNavigate}
        title={title}
        // Marked by the model's section below while it is open, so two rows are never both lit.
        className={() =>
          `project-tree__link project-tree__link--model${isOpen ? ' is-open' : ''}`
        }
      >
        <AppIcon name="cube" size={13} />
        <span>{title}</span>
      </NavLink>
      {isOpen ? (
        <ul className="project-tree__sections" aria-label={`${title} sections`}>
          {visibleModelSections(canManage, kind).map((section) => (
            <li key={section.id}>
              <NavLink
                to={`${base}?tab=${section.id}`}
                onClick={onNavigate}
                className={`project-tree__link project-tree__link--section${active === section.id ? ' is-active' : ''}`}
                aria-current={active === section.id ? 'page' : undefined}
              >
                <span aria-hidden="true" className="project-tree__branch" />
                <span>{section.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * A project's sections, with the models it links listed under Models. The models come from the
 * project itself — the same request its pages make — so a newly linked model appears without
 * anything being refreshed here.
 */
function ProjectBranch({
  project,
  capabilities,
  currentId,
  currentModelId,
  onNavigate,
  id,
}: {
  readonly capabilities: { readonly projectDetails: boolean };
  readonly project: RecentProject;
  readonly currentId: string | null;
  readonly currentModelId: number | null;
  readonly onNavigate: () => void;
  readonly id: string;
}): React.JSX.Element {
  const detail = useWorkspaceProject(toProjectId(project.id), true).data;
  const titles = new Map((detail?.models ?? []).map((model) => [Number(model.id), model.title]));
  const modelIds = detail?.modelIds ?? [];
  const insideModel = project.id === currentId && currentModelId !== null;

  return (
    <ul id={id} className="project-tree__sections" aria-label={`${project.name} sections`}>
      {sectionsOf(project, capabilities).map((section) => (
        <Fragment key={section.to}>
          <li>
            <NavLink
              to={section.to}
              // While a model is open the Models link is only the place it sits in, not the page.
              end={section.end ?? insideModel}
              onClick={onNavigate}
              className={({ isActive }) =>
                `project-tree__link project-tree__link--section${isActive ? ' is-active' : ''}`
              }
            >
              <span aria-hidden="true" className="project-tree__branch" />
              <span>{section.label}</span>
              {modelIds.length === 0 ? null : (
                <span className="project-tree__count" aria-hidden="true">
                  {String(modelIds.length)}
                </span>
              )}
            </NavLink>
          </li>
          {section.to === ROUTES.projectModels(project.id) && modelIds.length > 0 ? (
            <li className="project-tree__models">
              <ul aria-label={`${project.name} models`} className="project-tree__sections">
                {modelIds.map((modelId) => (
                  <ModelItem
                    key={modelId}
                    projectId={project.id}
                    modelId={Number(modelId)}
                    title={titles.get(Number(modelId)) ?? `Model ${String(modelId)}`}
                    isOpen={project.id === currentId && currentModelId === Number(modelId)}
                    canManage={project.role === 'ProjectAdmin'}
                    onNavigate={onNavigate}
                  />
                ))}
              </ul>
            </li>
          ) : null}
        </Fragment>
      ))}
    </ul>
  );
}

/**
 * The sidebar's Projects entry as a tree: the ten projects opened most recently, each of which
 * opens to its sections. The current project is always open, so the person can see where they
 * are; "Projects" itself still goes to the full list, which is where the rest are found.
 */
export function WorkspaceProjectTree({
  label,
  compact,
  onNavigate,
  onExpandSidebar,
}: SidebarProjectSlotProps): React.JSX.Element {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { capabilities } = useCapabilities();
  const owner = user === null ? 'anonymous' : String(user.id);
  const currentId = matchPath('/p/:projectId/*', pathname)?.params.projectId ?? null;
  const modelParam = matchPath('/p/:projectId/models/:modelId/*', pathname)?.params.modelId;
  const currentModelId = modelParam === undefined ? null : Number(modelParam);

  useRecordCurrentProject(owner, currentId);

  const recent = useSyncExternalStore(
    subscribeRecentProjects,
    () => readRecentProjects(owner),
    () => []
  );

  const [expanded, setExpanded] = useState(() => matchPath('/p/*', pathname) !== null);
  const [openProjects, setOpenProjects] = useState<ReadonlySet<string>>(() => new Set());
  const childrenId = useId();
  const open = !compact && expanded;

  // Reveal the current project on navigation, back/forward and deep links, while still letting
  // the person close a branch they are inside.
  useEffect(() => {
    if (currentId === null) return;
    setExpanded(true);
    setOpenProjects((current) => (current.has(currentId) ? current : new Set(current).add(currentId)));
  }, [currentId]);

  const toggleProject = (projectId: string): void => {
    setOpenProjects((current) => {
      const next = new Set(current);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  return (
    <div className="project-tree">
      <div className={`project-tree__heading${compact ? ' project-tree__heading--compact' : ''}`}>
        {compact ? null : (
          <NavLink
            to={ROUTES.workspace}
            end
            onClick={onNavigate}
            className={({ isActive }) => `app-sidebar__item${isActive ? ' is-active' : ''}`}
          >
            <span className="app-sidebar__icon">
              <AppIcon name="projects" size={20} />
            </span>
            <span className="app-sidebar__label">{label}</span>
          </NavLink>
        )}
        <button
          type="button"
          className="project-tree__toggle"
          aria-label={open ? 'Collapse projects' : 'Expand projects'}
          aria-expanded={open}
          aria-controls={childrenId}
          title={compact ? 'Expand projects' : undefined}
          onClick={() => {
            if (compact) {
              setExpanded(true);
              onExpandSidebar();
            } else {
              setExpanded((value) => !value);
            }
          }}
        >
          <AppIcon name={compact ? 'projects' : open ? 'chevronUp' : 'chevronDown'} size={compact ? 20 : 16} />
        </button>
      </div>

      <div id={childrenId} hidden={!open} className="project-tree__children">
        {open ? (
          recent.length === 0 ? (
            <p className="project-tree__status" role="status">
              Projects you open appear here.
            </p>
          ) : (
            <ul className="project-tree__list project-tree__list--recent" aria-label="Recent projects">
              {recent.map((project) => {
                const isOpen = openProjects.has(project.id);
                const sectionsId = `${childrenId}-${project.id}`;

                return (
                  <li key={project.id}>
                    <div className="project-tree__node">
                      <button
                        type="button"
                        className="project-tree__node-toggle"
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${project.name}`}
                        aria-expanded={isOpen}
                        aria-controls={sectionsId}
                        onClick={() => {
                          toggleProject(project.id);
                        }}
                      >
                        <AppIcon name={isOpen ? 'chevronDown' : 'chevronRight'} size={14} />
                      </button>
                      <NavLink
                        to={ROUTES.projectHome(project.id)}
                        end
                        onClick={onNavigate}
                        title={`${project.name} (${project.code})`}
                        className={({ isActive }) =>
                          `project-tree__link project-tree__link--project${isActive ? ' is-active' : ''}`
                        }
                      >
                        <span>{project.name}</span>
                      </NavLink>
                    </div>
                    {isOpen ? (
                      <ProjectBranch
                        id={sectionsId}
                        project={project}
                        capabilities={capabilities}
                        currentId={currentId}
                        currentModelId={currentModelId}
                        onNavigate={onNavigate}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )
        ) : null}
      </div>
    </div>
  );
}
